/**
 * Pure form-state mappers for the Organisation page. Lives outside the page
 * itself so the (largely orthogonal) wire-format conversion can be tested
 * and reasoned about independently of the React surface — the page reads as
 * orchestration, this file reads as plumbing.
 *
 * Every helper here is a pure function: server DTO → form state, or form
 * state → wire request. No React, no toasts, no side effects.
 */
import type {
  Address,
  CreateAddressRequest,
  CreateSiteRequest,
  Organisation,
  OrganisationSequence,
  SequenceTypeValue,
  UpdateAddressRequest,
  UpdateOrganisationRequest,
  UpdateOrganisationSequenceRequest,
  AccountingSystemValue,
  PaymentTermBasisValue,
  VatRoundingMethodValue,
} from '@my/directory-api';
import type { ExtraField } from '@my/ui';
import {
  DEFAULT_PREFIX_BY_TYPE,
  EDITOR_SEQUENCE_TYPES,
  type SequenceConfigFormValue,
  type SequenceFormFields,
} from '../SequenceConfigEditor';

/**
 * Parse the payment-term day count from its form string. Blank / negative /
 * non-numeric falls back to 30 — never 0, which the wording treats as "due on
 * receipt" and would silently change what customers are told.
 */
export function parsePaymentTermDays(value: string): number {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 30;
}

// ────────────────────────────────────────────────────────────────────────────
// Form types
// ────────────────────────────────────────────────────────────────────────────

export interface OrganisationForm {
  name: string;
  invoicingName: string;
  networkId: string;
  taxNumber: string;
  registrationNumber: string;
  currencyIsoCode: string;
  accountingSystem: AccountingSystemValue;
  vatRoundingMethod: VatRoundingMethodValue;
  /** Tag charge and invoice lines with an accounting category beside the nominal. */
  useAccountingCategories: boolean;
  /** House payment terms — the fallback for customers with none of their own. */
  defaultPaymentTermDays: string;
  defaultPaymentTermBasis: PaymentTermBasisValue;
  operationalTimeZone: string;
  languageCode: string;
  timePattern: string;
  datePattern: string;
  extraFields: ExtraField[];
}

export interface AddressForm {
  code: string;
  fullAddress: string;
  city: string;
  postcode: string;
  countryIsoCode: string;
  latitude: number | null;
  longitude: number | null;
  placeId: string;
  /**
   * Where the coordinates came from: `Google` (picked / Find on map) or
   * `Manual` (pin dragged or tapped). Forwarded on save so Directory never
   * refreshes a hand-placed pin back off the gate.
   */
  coordinatesSource: 'Google' | 'Manual' | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Form defaults
// ────────────────────────────────────────────────────────────────────────────

export const EMPTY_ADDRESS_FORM: AddressForm = {
  code: '',
  fullAddress: '',
  city: '',
  postcode: '',
  countryIsoCode: 'IE',
  latitude: null,
  longitude: null,
  placeId: '',
  coordinatesSource: null,
};

export const EMPTY_SEQUENCE_CONFIG: SequenceConfigFormValue = EDITOR_SEQUENCE_TYPES.reduce(
  (acc, type) => {
    acc[type] = {
      format: 'Plain',
      prefix: DEFAULT_PREFIX_BY_TYPE[type],
      currentNumber: '1',
      isLocked: false,
    };
    return acc;
  },
  {} as SequenceConfigFormValue,
);

export function isSequenceFormPristine(form: SequenceFormFields, type: SequenceTypeValue): boolean {
  return (
    form.format === 'Plain' &&
    form.prefix === DEFAULT_PREFIX_BY_TYPE[type] &&
    form.currentNumber === '1' &&
    !form.isLocked
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Server DTO → form state
// ────────────────────────────────────────────────────────────────────────────

export function parseExtraFields(raw: unknown): ExtraField[] {
  if (raw == null) return [];
  let text: string;
  if (typeof raw === 'object') {
    const nested = (raw as { extraFields?: unknown }).extraFields;
    text = typeof nested === 'string' ? nested : '';
  } else if (typeof raw === 'string') {
    text = raw;
  } else {
    return [];
  }
  if (!text.trim()) return [];
  try {
    const parsed = JSON.parse(text) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is ExtraField =>
        typeof x === 'object' &&
        x !== null &&
        typeof (x as ExtraField).fieldLabel === 'string' &&
        typeof (x as ExtraField).fieldValue === 'string' &&
        ['text', 'number', 'date', 'boolean'].includes((x as ExtraField).fieldType as string),
    );
  } catch {
    return [];
  }
}

export function toOrgForm(org: Organisation): OrganisationForm {
  return {
    name: org.name ?? '',
    invoicingName: org.invoicingName ?? '',
    networkId: org.networkId ?? '',
    taxNumber: org.taxNumber ?? '',
    registrationNumber: org.registrationNumber ?? '',
    currencyIsoCode: org.currencyIsoCode ?? 'EUR',
    accountingSystem: org.accountingSystem ?? 'None',
    vatRoundingMethod: org.vatRoundingMethod ?? 'Total',
    useAccountingCategories: org.useAccountingCategories ?? false,
    // Kept as a string so the number input can be emptied mid-edit without
    // snapping to 0; parsed back on save.
    defaultPaymentTermDays: String(org.defaultPaymentTermDays ?? 30),
    defaultPaymentTermBasis: org.defaultPaymentTermBasis ?? 'InvoiceDate',
    operationalTimeZone: org.operationalTimeZone ?? 'Europe/Dublin',
    languageCode: org.languageCode ?? 'en',
    timePattern: org.timePattern ?? 'HH:mm',
    datePattern: org.datePattern ?? 'dd/MM/yyyy',
    extraFields: parseExtraFields(org.extraFields),
  };
}

export function toAddressForm(address: Address): AddressForm {
  return {
    code: address.code ?? '',
    fullAddress: address.fullAddress ?? '',
    city: address.city ?? '',
    postcode: address.postcode ?? '',
    countryIsoCode: address.countryIsoCode ?? '',
    latitude: address.latitude ?? null,
    longitude: address.longitude ?? null,
    placeId: address.placeId ?? '',
    coordinatesSource: address.coordinatesSource ?? null,
  };
}

export function toSequenceForm(
  seq: OrganisationSequence | undefined,
  fallbackPrefix: string,
): SequenceFormFields {
  if (!seq) {
    return { format: 'Plain', prefix: fallbackPrefix, currentNumber: '1', isLocked: false };
  }
  return {
    format: seq.format,
    prefix: seq.prefix,
    currentNumber: String(seq.currentNumber),
    isLocked: seq.isLocked,
  };
}

export function toSequenceConfigForm(
  sequences: readonly OrganisationSequence[],
): SequenceConfigFormValue {
  return EDITOR_SEQUENCE_TYPES.reduce((acc, type) => {
    const existing = sequences.find((s) => s.type === type);
    acc[type] = toSequenceForm(existing, DEFAULT_PREFIX_BY_TYPE[type]);
    return acc;
  }, {} as SequenceConfigFormValue);
}

// ────────────────────────────────────────────────────────────────────────────
// Form state → wire request
// ────────────────────────────────────────────────────────────────────────────

export function toUpdateOrganisationRequest(
  org: Organisation,
  form: OrganisationForm,
  mainOfficeAddressId: string | null = org.mainOfficeAddressId ?? null,
  /**
   * The main office's country, which is where the country is actually edited —
   * the organisation form has no country field of its own.
   *
   * <p>
   * Passed in because the organisation's own `countryIsoCode` drives the tax
   * schedule and the nominal-code defaults, and it used to be echoed straight
   * back from the loaded record: changing the address country left it on
   * whatever the organisation was created with (IE by default), so the tax
   * section kept offering the wrong jurisdiction with nothing to show why.
   * </p>
   *
   * <p>
   * Omitted or blank keeps the current value rather than blanking a required
   * column — an address cleared mid-edit must not wipe the country.
   * </p>
   */
  mainOfficeCountryIsoCode?: string | null,
): UpdateOrganisationRequest {
  const countryIsoCode =
    (mainOfficeCountryIsoCode ?? '').trim().toUpperCase() || org.countryIsoCode;
  return {
    organisationId: org.id,
    name: form.name.trim(),
    invoicingName: form.invoicingName.trim(),
    taxNumber: form.taxNumber.trim() || null,
    registrationNumber: form.registrationNumber.trim() || null,

    operationalTimeZone: form.operationalTimeZone,
    languageCode: form.languageCode,
    currencyIsoCode: form.currencyIsoCode,
    countryIsoCode,
    timePattern: form.timePattern,
    datePattern: form.datePattern,

    additionalCurrencyIsoCodes: null,
    // Serialise to JSON so the BE round-trips it as opaque text. Empty
    // array sends `null` instead of `"[]"` — keeps unused orgs free of
    // placeholder payload and matches the inverse parse in
    // `parseExtraFields`.
    extraFields: form.extraFields.length > 0 ? JSON.stringify(form.extraFields) : null,

    accountingSystem: form.accountingSystem,
    vatRoundingMethod: form.vatRoundingMethod,
    // Sent from here only. Other apps' organisation screens leave it out, and
    // the server keeps the stored value when it is absent.
    useAccountingCategories: form.useAccountingCategories,

    // A blank or nonsense day count falls back to 30 rather than sending 0,
    // which would mean "due on receipt" — a very different promise.
    defaultPaymentTermDays: parsePaymentTermDays(form.defaultPaymentTermDays),
    defaultPaymentTermBasis: form.defaultPaymentTermBasis,

    mainOfficeAddressId,
  };
}

/**
 * Build the POST body for `/addresses` from the address form. Used by the
 * Organisation page's "add main office address" flow. Server-side defaults
 * (status=Active, addressType=Primary) are spelt out explicitly here so the
 * created address always lines up with the org's `mainOfficeAddressId`
 * pointer.
 */
export function toCreateAddressRequest(
  organisationId: string,
  form: AddressForm,
): CreateAddressRequest {
  return {
    organisationId,
    addressType: 'Primary',
    name: 'Main Office',
    code: form.code.trim() || null,
    fullAddress: form.fullAddress.trim(),
    city: form.city.trim() || null,
    postcode: form.postcode.trim() || null,
    countryIsoCode: form.countryIsoCode.trim().toUpperCase(),
    latitude: form.latitude,
    longitude: form.longitude,
    coordinatesSource: form.coordinatesSource ?? null,
    placeId: form.placeId.trim(),
    status: 'Active',
  };
}

/**
 * Build the POST body for `/sites`. The Organisation page always seeds a
 * Site of `Type=Site` alongside the main office Address — every Address row
 * in the directory belongs to exactly one Site, so creating an address
 * without a paired Site leaves orphan data the rest of the suite can't
 * point at (vehicle home base, parties, etc.).
 */
export function toCreateSiteRequest(organisationId: string, addressId: string): CreateSiteRequest {
  return {
    organisationId,
    name: 'Main Office',
    type: 'Site',
    addressId,
    status: 'Active',
  };
}

export function toUpdateAddressRequest(address: Address, form: AddressForm): UpdateAddressRequest {
  return {
    addressId: address.id,
    addressType: address.addressType,
    name: address.name,
    // Code is editable from the OrganisationPage's AddressInlineEditor —
    // pull it from the form rather than echoing the server value, otherwise
    // operator edits get silently dropped.
    code: form.code.trim() || null,
    fullAddress: form.fullAddress.trim(),
    city: form.city.trim() || null,
    postcode: form.postcode.trim() || null,
    countryIsoCode: form.countryIsoCode.trim().toUpperCase(),
    latitude: form.latitude,
    longitude: form.longitude,
    coordinatesSource: form.coordinatesSource ?? null,
    placeId: form.placeId.trim(),
    status: address.status,
    serviceTags: address.serviceTags ?? null,
  };
}

/**
 * Build the PUT body for `/organisationSequences`. Echoes the unchanged
 * server fields so the BE doesn't snap them to its defaults; the only
 * editable surface from the form is prefix / currentNumber / isLocked.
 *
 * `currentNumber` is sent regardless — the BE handler ignores the inbound
 * value when the sequence is already locked, so the client doesn't have to
 * special-case it.
 */
export function toUpdateSequenceRequest(
  existing: OrganisationSequence,
  form: SequenceFormFields,
): UpdateOrganisationSequenceRequest {
  const parsedCurrent = parseInt(form.currentNumber, 10);
  // YearMonth ignores Prefix on render — send empty to keep the wire shape
  // honest. The BE validator only requires Prefix when Format is Plain.
  const prefix = form.format === 'YearMonth' ? '' : form.prefix.trim();
  return {
    sequenceId: existing.id,
    type: existing.type,
    format: form.format,
    prefix,
    suffix: existing.suffix ?? null,
    currentNumber: Number.isFinite(parsedCurrent)
      ? Math.max(0, parsedCurrent)
      : existing.currentNumber,
    increment: existing.increment,
    minValue: existing.minValue,
    maxValue: existing.maxValue ?? null,
    resetAnnually: existing.resetAnnually,
    padding: existing.padding ?? 'D6',
    status: existing.status,
    // Form `isLocked` is OR-ed with the server's view: once locked
    // server-side we never propose to unlock (the BE 409s anyway).
    isLocked: existing.isLocked || form.isLocked,
  };
}
